import React, { memo, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isDark?: boolean;
}

interface CodeBlockProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

// Memoized code block component to prevent unnecessary re-renders
const CodeBlock: React.FC<CodeBlockProps & { isDark?: boolean }> = memo(({ inline, className, children, isDark = true }) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeString = String(children).replace(/\n$/, '');

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(codeString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [codeString]);

  if (inline) {
    return (
      <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${isDark ? 'bg-dark-700 text-purple-300' : 'bg-slate-200 text-purple-600'}`}>
        {children}
      </code>
    );
  }

  // Memoize the custom styles to prevent object recreation
  const customStyle = useMemo(() => ({
    margin: 0,
    padding: '1rem',
    background: isDark ? '#1a1b26' : '#f8fafc',
    fontSize: '0.875rem',
    lineHeight: '1.6',
    // Prevent layout shifts during streaming
    minHeight: '3rem',
    overflow: 'auto',
  }), [isDark]);

  const codeTagStyle = useMemo(() => ({
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  }), []);

  return (
    <div className={`relative group my-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-300'}`} style={{ contain: 'layout style' }}>
      {/* Header with language and copy button */}
      <div className={`flex items-center justify-between px-4 py-2 border-b ${isDark ? 'bg-dark-700 border-white/10' : 'bg-slate-200 border-slate-300'}`}>
        <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          {language || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-300'}`}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-green-400" />
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code content - use wrapLines to prevent layout shifts */}
      <SyntaxHighlighter
        style={isDark ? oneDark : oneLight}
        language={language || 'text'}
        PreTag="div"
        customStyle={customStyle}
        codeTagProps={{
          style: codeTagStyle,
        }}
        wrapLines={true}
        wrapLongLines={true}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  );
});

CodeBlock.displayName = 'CodeBlock';

const MarkdownRenderer: React.FC<MarkdownRendererProps> = memo(({ content, className = '', isDark = true }) => {
  // Create a wrapper component that passes isDark to CodeBlock
  const CodeBlockWithTheme = useCallback((props: React.ClassAttributes<HTMLElement> & React.HTMLAttributes<HTMLElement> & { inline?: boolean }) => {
    return <CodeBlock {...props} isDark={isDark} />;
  }, [isDark]);

  return (
    <div className={`markdown-content overflow-hidden break-words ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Code blocks with syntax highlighting
        code: CodeBlockWithTheme as React.ComponentType<React.ClassAttributes<HTMLElement> & React.HTMLAttributes<HTMLElement>>,

        // Headings
        h1: ({ children }) => (
          <h1 className={`text-xl font-bold mt-6 mb-3 first:mt-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className={`text-lg font-semibold mt-5 mb-2 first:mt-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className={`text-base font-semibold mt-4 mb-2 first:mt-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 className={`text-sm font-semibold mt-3 mb-1.5 first:mt-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</h4>
        ),

        // Paragraphs
        p: ({ children }) => (
          <p className={`leading-relaxed mb-3 last:mb-0 break-words ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{children}</p>
        ),

        // Lists
        ul: ({ children }) => (
          <ul className={`list-disc list-inside space-y-1.5 mb-3 ml-1 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className={`list-decimal list-inside space-y-1.5 mb-3 ml-1 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{children}</ol>
        ),
        li: ({ children }) => (
          <li className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{children}</li>
        ),

        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline underline-offset-2 transition-colors ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
          >
            {children}
          </a>
        ),

        // Emphasis
        strong: ({ children }) => (
          <strong className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</strong>
        ),
        em: ({ children }) => (
          <em className={`italic ${isDark ? 'text-gray-200' : 'text-slate-600'}`}>{children}</em>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote className={`border-l-4 border-purple-500 pl-4 py-1 my-3 rounded-r-lg ${isDark ? 'bg-purple-500/10' : 'bg-purple-100'}`}>
            {children}
          </blockquote>
        ),

        // Horizontal rule
        hr: () => <hr className={`my-6 ${isDark ? 'border-white/10' : 'border-slate-300'}`} />,

        // Tables
        table: ({ children }) => (
          <div className="overflow-x-auto my-4">
            <table className={`min-w-full border rounded-lg overflow-hidden ${isDark ? 'border-white/10' : 'border-slate-300'}`}>
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className={isDark ? 'bg-dark-700' : 'bg-slate-200'}>{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody className={`divide-y ${isDark ? 'divide-white/10' : 'divide-slate-200'}`}>{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr className={`transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}>{children}</tr>
        ),
        th: ({ children }) => (
          <th className={`px-4 py-2 text-left text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{children}</th>
        ),
        td: ({ children }) => (
          <td className={`px-4 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{children}</td>
        ),

        // Pre (for code blocks)
        pre: ({ children }) => (
          <>{children}</>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
